"use client"

import { useState } from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { INDUSTRIES } from "@/lib/industries"

interface IndustrySelectorProps {
  selectedIndustries: string[]
  onIndustriesChange: (industries: string[]) => void
  placeholder?: string
  label?: string
}

export function IndustrySelector({
  selectedIndustries,
  onIndustriesChange,
  placeholder = "Select industries...",
  label = "Industries",
}: IndustrySelectorProps) {
  const [open, setOpen] = useState(false)

  const handleSelect = (industry: string) => {
    if (selectedIndustries.includes(industry)) {
      onIndustriesChange(selectedIndustries.filter((item) => item !== industry))
    } else {
      onIndustriesChange([...selectedIndustries, industry])
    }
  }

  const handleRemove = (industry: string) => {
    onIndustriesChange(selectedIndustries.filter((item) => item !== industry))
  }

  const clearAll = () => {
    onIndustriesChange([])
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-teal-600">{label}</Label>
        {selectedIndustries.length > 0 && (
          <Button variant="link" size="sm" className="text-teal-600 p-0 h-auto" onClick={clearAll}>
            Clear all
          </Button>
        )}
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between text-left font-normal bg-transparent"
          >
            {selectedIndustries.length > 0 ? `${selectedIndustries.length} selected` : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search industries..." />
            <CommandList>
              <CommandEmpty>No industries found.</CommandEmpty>
              <CommandGroup>
                {INDUSTRIES.map((industry) => (
                  <CommandItem
                    key={industry}
                    value={industry}
                    onSelect={() => handleSelect(industry)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedIndustries.includes(industry) ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {industry}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedIndustries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedIndustries.map((industry) => (
            <Badge key={industry} variant="secondary" className="flex items-center gap-1">
              <span className="text-xs bg-teal-700 text-white px-1 rounded">INDUSTRY</span>
              {industry}
              <X className="h-3 w-3 cursor-pointer" onClick={() => handleRemove(industry)} />
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
